import type { RecipeDef, StageDef } from "@reagent/sim";
import { freshCardInstances } from "@reagent/sim";
import { ALL_CARDS } from "../../cards";
import { pollutionCardsFor, reachablePollutionReasons } from "../../pollution";
import { setupOfferFor, validateStage } from "../../schema";
import type { StageSolution } from "../../solutions";
import { PF_STEEP_BUFFER, PF_STEEP_CRITICAL_TEMP } from "./machines";
import { ERA_1_SCALARS } from "./shared";

/**
 * Rhodinon & unguents (PF), Era 1 (spec 0037, docs/design/06-eras-and-stages.md
 * section 4.3/4.6). Every number below is a PLACEHOLDER (spec 0037 "Numbers"):
 * chosen so the worked solutions clear, and listed in the worklog as needing
 * playtest. The core teaches Cooling (discipline); the refined tier teaches Heat.
 */

// PLACEHOLDER (spec 0037): needs playtest. Ratios, yields and heat per firing.
// The yields are lopsided (5 Rhodinon and 1 Oxide make 10 RawSalve) on purpose:
// a Budget feed's Sludge rides into the product, and the hopper's 70% purity
// floor has to survive it even when the market doubles the impurity.
/** Warm steep: petals feed at ratio `a` = 2. Exothermic, so the pre-placed reactor's low criticalTemp is what bites. */
export const PF_STEEP: RecipeDef = {
  id: "pf-steep",
  inputs: { Omphacium: 1, Petals: 2, Fuel: 1 },
  outputs: { Rhodinon: 2, Alkali: 1 },
  isExothermic: true,
  heat: 1,
  nodeKinds: ["reactor"],
};

/** Press: `Rhodinon` down `main`, the `Alkali` export and any Budget Sludge down `waste`. */
export const PF_PRESS: RecipeDef = {
  id: "pf-press",
  inputs: { Rhodinon: 2, Alkali: 1 },
  outputs: { Rhodinon: 2, Alkali: 1 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["separator"],
  primarySpecies: ["Rhodinon"],
};

/** Salve steep: cold on purpose, so the refined tier's lesson is the heat gate and not a second cooling problem. */
export const PF_SALVE: RecipeDef = {
  id: "pf-salve",
  inputs: { Rhodinon: 5, Oxide: 1 },
  outputs: { RawSalve: 10 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["reactor"],
};

// PLACEHOLDER (spec 0037): needs playtest. One heater is +30 C on a packet born
// at 20 C, so the gate sits exactly on what a single heater reaches.
/** Salve cook: RawSalve becomes Salve only at 50 C or above. */
export const PF_SALVE_HOLD: RecipeDef = {
  id: "pf-salve-hold",
  inputs: { RawSalve: 1 },
  outputs: { Salve: 1 },
  isExothermic: false,
  heat: 0,
  nodeKinds: ["incubator"],
  holdDuration: 2,
  minTemperature: 50,
};

export const PF_RECIPES: readonly RecipeDef[] = [PF_STEEP, PF_PRESS, PF_SALVE, PF_SALVE_HOLD];

const card = (id: string) => {
  const found = ALL_CARDS.find((c) => c.id === id);
  if (!found) throw new Error(`pf: no card ${id}`);
  return found;
};

/** An authored pipe path from an explicit tile list: the detours below are what equalise the feed lengths. */
const walk = (...points: readonly (readonly [number, number])[]): { x: number; y: number }[] =>
  points.map(([x, y]) => ({ x, y }));

/**
 * Core: the warm steep. The reactor and the press are pre-placed: no catalogue
 * card gives a reactor this low a criticalTemp, and the press is a split node.
 *
 * Every feed pipe is four steps long so the three packets land on the same
 * tick. That is load-bearing, not tidy: a Budget packet carries Sludge, so the
 * reactor does not treat it as a reagent to wait with, and one that lands early
 * is shipped on unreacted.
 */
const CORE_BARE: StageDef = {
  id: "era1-pf-core",
  name: "Rhodinon Steep",
  blurb: "Rose petals steeped warm in oil: too hot and the batch scorches, too cold and it never takes.",
  cols: 8,
  rows: 5,
  nodes: [
    { id: "pf-c-in-omphacium", kind: "inputTank", pos: { x: 0, y: 0 }, emits: { Omphacium: 1 } },
    {
      id: "pf-c-in-petals",
      kind: "inputTank",
      pos: { x: 0, y: 2 },
      emits: { Petals: 2 },
      // PLACEHOLDER (spec 0037): needs playtest. Prices and the Sludge unit count.
      sourcing: { budget: { impurities: { Sludge: 1 }, costCredits: 4 }, premium: { costCredits: 8 } },
    },
    {
      id: "pf-c-in-fuel",
      kind: "inputTank",
      pos: { x: 0, y: 4 },
      emits: { Fuel: 1 },
      // PLACEHOLDER (spec 0037): needs playtest. Prices and the Sludge unit count.
      sourcing: { budget: { impurities: { Sludge: 1 }, costCredits: 3 }, premium: { costCredits: 6 } },
    },
    {
      id: "pf-c-steep",
      kind: "reactor",
      pos: { x: 2, y: 2 },
      // Config lives in machines.ts so the bound card reads the same numbers (spec 0068).
      criticalTemp: PF_STEEP_CRITICAL_TEMP,
      bufferCapacity: PF_STEEP_BUFFER,
      recipeIds: ["pf-steep"],
    },
    { id: "pf-c-press", kind: "separator", pos: { x: 4, y: 2 }, recipeIds: ["pf-press"] },
    { id: "pf-c-out", kind: "productHopper", pos: { x: 7, y: 1 }, bufferCapacity: 99 },
    { id: "pf-c-export", kind: "wasteBuyer", pos: { x: 7, y: 3 }, bufferCapacity: 99 },
  ],
  edges: [
    { id: "pf-c-e-omphacium", fromNodeId: "pf-c-in-omphacium", toNodeId: "pf-c-steep", path: walk([0, 0], [1, 0], [2, 0], [2, 1], [2, 2]) },
    // The detour spends two extra steps so this run matches the others' length.
    { id: "pf-c-e-petals", fromNodeId: "pf-c-in-petals", toNodeId: "pf-c-steep", path: walk([0, 2], [0, 1], [1, 1], [1, 2], [2, 2]) },
    { id: "pf-c-e-fuel", fromNodeId: "pf-c-in-fuel", toNodeId: "pf-c-steep", path: walk([0, 4], [1, 4], [2, 4], [2, 3], [2, 2]) },
    { id: "pf-c-e-press", fromNodeId: "pf-c-steep", toNodeId: "pf-c-press", path: walk([2, 2], [3, 2], [4, 2]) },
    {
      id: "pf-c-e-main",
      fromNodeId: "pf-c-press",
      toNodeId: "pf-c-out",
      path: walk([4, 2], [4, 1], [5, 1], [6, 1], [7, 1]),
      separationRole: "main",
    },
    {
      id: "pf-c-e-waste",
      fromNodeId: "pf-c-press",
      toNodeId: "pf-c-export",
      path: walk([4, 2], [4, 3], [5, 3], [6, 3], [7, 3]),
      separationRole: "waste",
    },
  ],
  recipes: [PF_STEEP, PF_PRESS],
  objective: {
    // PLACEHOLDER (spec 0037): needs playtest. Two firings' worth.
    targetComposition: { Rhodinon: 4 },
    minPurity: ERA_1_SCALARS.minPurity,
  },
  energyBudgetKw: ERA_1_SCALARS.energyBudgetKw,
  handSize: ERA_1_SCALARS.handSize,
  ticksPerTurn: ERA_1_SCALARS.ticksPerTurn,
  maxTurns: ERA_1_SCALARS.maxTurns,
  liveBuild: ERA_1_SCALARS.liveBuild,
  corrosiveSpecies: [],
  startingDeck: freshCardInstances([
    "card-cooling-jacket",
    "card-insulation",
    "card-low-fire",
    "card-flash-freeze",
    "card-solvent-flush",
  ]),
  // PLACEHOLDER (spec 0037): needs playtest. Covers both Budget contracts at the market's 2x ceiling (7 x 2 = 14).
  setupBudgetCredits: 20,
  setupCatalogue: [card("card-pf-steep"), card("card-mixer"), card("card-mixer-heavy")].map(setupOfferFor),
};

export const PF_CORE: StageDef = validateStage(
  { ...CORE_BARE, pollutionCards: pollutionCardsFor(...reachablePollutionReasons(CORE_BARE, ALL_CARDS)) },
  ALL_CARDS,
);

/**
 * Refined: the valve splits Rhodinon between its order (straight to the
 * hopper) and the salve steep. The valve is pre-placed because `NodeDef`
 * carries no valve setting, so it plays out on the default round-robin.
 *
 * Oxide arrives every tick but Rhodinon only every other one, so half the
 * Oxide finds no partner and is shipped on down the line as waste. Holding it
 * back is not an option: Budget Sludge stops the reactor treating it as a
 * reagent to wait with.
 */
const REFINED_BARE: StageDef = {
  id: "era1-pf-refined",
  name: "Rhodinon Salve",
  blurb: "Half the rose oil goes to the perfumer; the rest is cooked into a salve.",
  cols: 12,
  rows: 5,
  nodes: [
    {
      id: "pf-r-in-rhodinon",
      kind: "inputTank",
      pos: { x: 0, y: 1 },
      // PLACEHOLDER (spec 0037): needs playtest. Five units so plain Rhodinon still clears the
      // 70% purity floor with one (or, at the market's ceiling, two) units of Sludge riding along.
      emits: { Rhodinon: 5 },
      sourcing: { budget: { impurities: { Sludge: 1 }, costCredits: 4 }, premium: { costCredits: 8 } },
    },
    {
      id: "pf-r-in-oxide",
      kind: "inputTank",
      pos: { x: 0, y: 3 },
      emits: { Oxide: 1 },
      // PLACEHOLDER (spec 0037): needs playtest. Prices and the Sludge unit count.
      sourcing: { budget: { impurities: { Sludge: 1 }, costCredits: 3 }, premium: { costCredits: 6 } },
    },
    { id: "pf-r-valve", kind: "flowMover", pos: { x: 2, y: 1 } },
    { id: "pf-r-salve", kind: "reactor", pos: { x: 4, y: 2 }, recipeIds: ["pf-salve"] },
    { id: "pf-r-out", kind: "productHopper", pos: { x: 11, y: 2 }, bufferCapacity: 99 },
  ],
  edges: [
    { id: "pf-r-e-feed", fromNodeId: "pf-r-in-rhodinon", toNodeId: "pf-r-valve", path: walk([0, 1], [1, 1], [2, 1]) },
    // Ids sort order-before-steep, so the first packet is the perfumer's.
    {
      id: "pf-r-e-order",
      fromNodeId: "pf-r-valve",
      toNodeId: "pf-r-out",
      path: walk([2, 1], [2, 0], [3, 0], [4, 0], [5, 0], [6, 0], [7, 0], [8, 0], [9, 0], [10, 0], [11, 0], [11, 1], [11, 2]),
    },
    { id: "pf-r-e-steep", fromNodeId: "pf-r-valve", toNodeId: "pf-r-salve", path: walk([2, 1], [3, 1], [4, 1], [4, 2]) },
    { id: "pf-r-e-oxide", fromNodeId: "pf-r-in-oxide", toNodeId: "pf-r-salve", path: walk([0, 3], [1, 3], [2, 3], [3, 3], [4, 3], [4, 2]) },
  ],
  recipes: [PF_SALVE, PF_SALVE_HOLD],
  objective: {
    // PLACEHOLDER (spec 0037): needs playtest. Two plain packets, and two salve batches of ten.
    targetComposition: { Rhodinon: 10, Salve: 20 },
    minPurity: ERA_1_SCALARS.minPurity,
  },
  energyBudgetKw: ERA_1_SCALARS.energyBudgetKw,
  handSize: ERA_1_SCALARS.handSize,
  ticksPerTurn: ERA_1_SCALARS.ticksPerTurn,
  maxTurns: ERA_1_SCALARS.maxTurns,
  liveBuild: ERA_1_SCALARS.liveBuild,
  corrosiveSpecies: [],
  startingDeck: freshCardInstances(["card-solvent-flush", "card-nitrogen-purge", "card-rush-order", "card-overtime"]),
  // PLACEHOLDER (spec 0037): needs playtest. Rig 40 (heater 10, incubator 15, three pipes 15)
  // plus both Budget contracts at the market's 2x ceiling (7 x 2 = 14).
  setupBudgetCredits: 60,
  setupCatalogue: [card("card-pf-salve"), card("card-mixer"), card("card-mixer-heavy"), card("card-heater"), card("card-incubator"), card("card-pipe-glass")].map(setupOfferFor),
};

export const PF_REFINED: StageDef = validateStage(
  { ...REFINED_BARE, pollutionCards: pollutionCardsFor(...reachablePollutionReasons(REFINED_BARE, ALL_CARDS)) },
  ALL_CARDS,
);

export const PF_CORE_SOLUTION: StageSolution = {
  stageId: PF_CORE.id,
  sourcing: { "pf-c-in-petals": "budget", "pf-c-in-fuel": "budget" },
  turns: [
    [
      // The jacket sheds the one heat a firing makes before it can reach the low criticalTemp;
      // insulation is the margin for a firing that ever pools.
      { cardId: "card-cooling-jacket", at: { x: 2, y: 2 } },
      { cardId: "card-insulation", at: { x: 2, y: 2 } },
    ],
  ],
  notes:
    "The steep runs at k=1 every tick and makes one heat a firing against a criticalTemp of 2, so an " +
    "uncooled reactor is over its limit on the second firing and explodes on the third (`03-hazards.md` " +
    "section 2.1). A Cooling Jacket keeps the counter at zero; Thermal Insulation adds three degrees of " +
    "margin. 3 kW, all in turn 1. The press does the rest: Rhodinon to the hopper, Alkali and Sludge to the export.",
};

export const PF_REFINED_SOLUTION: StageSolution = {
  stageId: PF_REFINED.id,
  sourcing: { "pf-r-in-rhodinon": "budget", "pf-r-in-oxide": "budget" },
  setupBuild: [
    { cardId: "card-heater", at: { x: 6, y: 2 } }, // mints n1
    { cardId: "card-incubator", at: { x: 8, y: 2 } }, // mints n2
    { cardId: "card-pipe-glass", fromNodeId: "pf-r-salve", toNodeId: "n1" },
    { cardId: "card-pipe-glass", fromNodeId: "n1", toNodeId: "n2" },
    { cardId: "card-pipe-glass", fromNodeId: "n2", toNodeId: "pf-r-out" },
  ],
  turns: [],
  notes:
    "RawSalve is born at 20 C whatever fed the reactor, so the heater has to sit between the reactor and " +
    "the incubator: one pass is +30 C, exactly the 50 C the cook needs. Without it the incubator lets " +
    "RawSalve straight through and the hopper scores none. Built and piped in Phase 1, so nothing is played.",
};
